// Chakra UI components and icons
import {
  Box,
  Center,
  Divider,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { RepeatIcon, SearchIcon } from '@chakra-ui/icons';

// Third-party libraries
import axios from 'axios';
import { AsyncSelect, Select } from 'chakra-react-select';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// Custom components and hooks
import FormikFormControl from 'components/common/forms/FormikFormControl';
import useToastNotif from 'hooks/useToastNotif';

// Utility functions and constants
import subQuestColumns from 'utils/columns/reports/subQuestColumns';
import { TOAST_ERROR } from 'utils/constants/toast';
import {
  INTERACTIVE_ID,
  NARRATIVE_ID,
  QUIZ_ID,
  VIDEO_ID,
  FILE_SUBMISSION_ID,
} from 'utils/constants/subQuestType';

function QuizReportPage() {
  // Set the document title for the page
  document.title = 'Skill Quest | SubQuest Reports';

  // React Router hooks for navigation and query parameters
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract course ID from query parameters
  const courseId = searchParams.get('course');

  // Options for filtering by type
  const typeOptions = [
    { label: 'Video', value: VIDEO_ID },
    { label: 'Interactive Content', value: INTERACTIVE_ID },
    { label: 'Quiz', value: QUIZ_ID },
    { label: 'Narrative', value: NARRATIVE_ID },
    { label: 'File Submission', value: FILE_SUBMISSION_ID },
  ];

  // State variables for managing filters, data, and UI state
  const [types, setTypes] = useState([]); // Selected types for filtering
  const [course, setCourse] = useState(null); // Selected course for filtering
  const [data, setData] = useState([]); // Sub-quest data
  const [courseData, setCourseData] = useState([]); // Sub-quests for a specific course
  const [loading, setLoading] = useState(false); // Loading state for API calls
  const [totalRows, setTotalRows] = useState(0); // Total rows for pagination
  const [perPage, setPerPage] = useState(10); // Rows per page
  const [orderBy, setOrderBy] = useState('asc'); // Order direction
  const [sortBy, setSortBy] = useState(''); // Column to sort by
  const [searchText, setSearchText] = useState(''); // Search input text

  // Filter course data based on search text and selected types
  const filteredCourseData = courseData.filter((item) => {
    const searchTextLower = searchText.toLowerCase();
    const typeIdValues = types.map((type) => type.value);
    const typeIdFilter = typeIdValues.length > 0;

    return (
      ((item.name && item.name.toLowerCase().includes(searchTextLower)) ||
        (item.objective &&
          item.objective.toLowerCase().includes(searchTextLower)) ||
        (item.code && item.code.toLowerCase().includes(searchTextLower)) ||
        (item.id &&
          item.id.toString().toLowerCase().includes(searchTextLower))) &&
      (!typeIdFilter || (item.type.id && typeIdValues.includes(item.type.id)))
    );
  });

  // Toast notification hook
  const { generateToastNotif } = useToastNotif();

  // Determine the theme for the data table based on color mode
  const tableTheme = useColorModeValue('default', 'dark');

  // Fetch sub-quest data from the server
  const fetchData = useCallback(
    async (params) => {
      setLoading(true);
      try {
        if (!course) {
          const {
            currentPage,
            pageSize,
            query,
            orderBy: orderDirection,
            sortBy: sortColumn,
          } = params;
          let queryParams = `?currentPage=${currentPage || 1}&pageSize=${pageSize || 10}`;
          if (query) queryParams += `&query=${query}`;
          if (orderDirection) queryParams += `&orderBy=${orderDirection}`;
          if (sortColumn) queryParams += `&sortBy=${sortColumn}`;

          if (types && types.length > 0) {
            for (let i = 0; i < types.length; i += 1)
              queryParams += `&typeIds=${types[i].value}`;
          } else queryParams += `&typeIds=1&typeIds=10&typeIds=4&typeIds=2`;

          const response = await axios.get(`/admin/sub-quests${queryParams}`);
          setData(response.data.data);
          setTotalRows(response.data.metaData.totalItems);
        }
      } catch {
        generateToastNotif(
          TOAST_ERROR,
          'Error',
          'Error fetching sub-quests',
          true,
          false
        );
      } finally {
        setLoading(false);
      }
    },
    [generateToastNotif, course, types] // Added `types` as a dependency
  );

  // Fetch sub-quests for a specific course
  const fetchCourseData = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await axios.get(`/admin/courses/${id}/sub-quests`);
        setCourseData(response.data);
      } catch {
        generateToastNotif(
          TOAST_ERROR,
          'Error',
          'Error fetching sub-quests',
          true,
          false
        );
      } finally {
        setLoading(false);
      }
    },
    [generateToastNotif]
  );

  // Handle changes in rows per page
  const handlePerRowsChange = (newPerPage, page) => {
    if (newPerPage === perPage) return;
    setPerPage(newPerPage);
    fetchData({
      currentPage: page,
      pageSize: newPerPage,
      query: searchText,
      orderBy,
      sortBy,
    });
  };

  // Handle sorting of table columns
  const handleSort = (column, sortDirection) => {
    if (!column?.sortField && sortDirection === orderBy) return;
    setOrderBy(sortDirection);
    setSortBy(column.sortField);
    fetchData({
      currentPage: 1,
      pageSize: perPage,
      orderBy: sortDirection,
      sortBy: column.sortField,
      query: searchText,
    });
  };

  // Handle page changes in the table
  const handlePageChange = (page) => {
    fetchData({
      currentPage: page,
      pageSize: perPage,
      query: searchText,
      orderBy,
      sortBy,
      types,
    });
  };

  // Generic handler for updating state with selected options
  const handleChange = (setter) => (selectedOptions) => {
    setter(selectedOptions);
  };

  // Handlers for course and type filters
  const handleCourseChange = handleChange(setCourse);
  const handleTypesChange = handleChange(setTypes);

  // Reload data based on current filters
  const handleReloadData = () => {
    if (course) {
      fetchCourseData(course.value);
    } else if (courseId) {
      fetchCourseData(courseId);
    } else {
      fetchData({ currentPage: 1 });
    }
  };

  // Remove course filter from query parameters
  const handleRemoveParams = () => {
    searchParams.delete('course');
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Load course options for the AsyncSelect component
  const loadCourses = debounce((inputValue, callback) => {
    axios
      .get(`/admin/courses?query=${inputValue.trim()}&pageSize=${5}`)
      .then((response) => {
        callback(
          response.data.data?.map((courseItem) => ({
            label: courseItem.name,
            value: courseItem.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 450);

  // Debounce search input to reduce API calls
  const debouncedFetchData = useMemo(
    () =>
      debounce((query) => {
        fetchData({
          currentPage: 1,
          pageSize: perPage,
          query,
        });
      }, 450),
    [fetchData, perPage]
  );

  // Handle changes in the search input
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    if (!course && !courseId) debouncedFetchData(e.target.value);
  };

  // Fetch initial data on component mount or when dependencies change
  useEffect(() => {
    if (course) {
      fetchCourseData(course.value);
    } else if (courseId) {
      fetchCourseData(courseId);
    }
  }, [fetchCourseData, course, courseId, types]); // Added `types` as a dependency

  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  // Render the page
  return (
    <Box minH="100vh" ml={{ base: 0, md: 20 }} p="10">
      {/* Page title */}
      <Text my={4} fontSize="2xl">
        Sub-Quest Reports
      </Text>

      {/* Filters section */}
      <Box p={4} boxShadow="md" borderWidth="1px" borderRadius="lg">
        <Text as="samp" fontSize="xs">
          <strong>SELECT FILTERS</strong>
        </Text>
        <Flex
          mt={4}
          justify="streched"
          direction={{ base: 'column', lg: 'row' }}
          gap={3}
        >
          {/* Course filter */}
          <Box w="100%">
            <FormikFormControl label="Courses" helperText="">
              <AsyncSelect
                loadOptions={loadCourses}
                value={course}
                onChange={handleCourseChange}
                isClearable
              />
            </FormikFormControl>
            {courseId && (
              <Tag borderRadius="full" variant="solid" colorScheme="blue">
                <TagLabel> Apply Filter: Course ID = {courseId}</TagLabel>
                <TagCloseButton onClick={handleRemoveParams} />
              </Tag>
            )}
          </Box>

          {/* Type filter */}
          <FormikFormControl label="Type" helperText="">
            <Select
              placeholder="All"
              value={types}
              onChange={handleTypesChange}
              options={typeOptions}
              isMulti
              isClearable
            />
          </FormikFormControl>
        </Flex>
      </Box>

      {/* Data table section */}
      <Box mt={5} w="100%" p={4} color="gray.500" borderWidth="1px">
        <Flex m={2}>
          <Spacer />

          {/* Reload data button */}
          <IconButton
            onClick={handleReloadData}
            aria-label="Reload table"
            variant="link"
            icon={<RepeatIcon />}
          />

          {/* Search input */}
          <InputGroup width={{ base: '100%', md: '350px' }}>
            <InputRightElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputRightElement>
            <Input
              value={searchText}
              onChange={handleSearchTextChange}
              name="query"
              placeholder="Search"
            />
          </InputGroup>
        </Flex>

        <Divider mt={4} />

        {/* Conditional rendering of the data table */}
        {course || courseId ? (
          <DataTable
            theme={tableTheme}
            progressPending={loading}
            columns={subQuestColumns}
            data={filteredCourseData}
            progressComponent={
              <Center my={10}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Center>
            }
            pagination
          />
        ) : (
          <DataTable
            theme={tableTheme}
            progressPending={loading}
            columns={subQuestColumns}
            data={data}
            paginationTotalRows={totalRows}
            pagination
            paginationServer
            sortServer
            onSort={handleSort}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            progressComponent={
              <Center my={10}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Center>
            }
          />
        )}
      </Box>
    </Box>
  );
}

export default QuizReportPage;
